import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { type ColDef } from "ag-grid-community";

import { ChartCard, KpiCard, KpiRow, MetricsGrid } from "./MetricsCards";
import {
  AXIS_PROPS,
  CHART_COLORS,
  CHART_LINE,
  Currency,
  LEGEND_PROPS,
  TOOLTIP_PROPS,
  downloadCsv,
  formatMonth,
  formatMoney,
  formatNumber,
  formatPercent,
} from "./metrics.utils";

import styles from "./CompanyMetricsTab.module.scss";

interface ITopClub {
  club_id: string;
  club_name: string;
  events: number;
  transactions: number;
  charged: number;
  fees: number;
  refunded: number;
  take_rate: number;
}

export interface IClubsData {
  totals: { total_clubs: number; new_clubs: number; active_clubs: number };
  new_clubs_by_month: { month: string; count: number; cumulative?: number }[];
  top_clubs: ITopClub[];
}

const PAGE_SIZE = 25;

const ClubNameCell: React.FC<{ data?: ITopClub }> = ({ data }) =>
  data ? <Link to={`/k/${data.club_id}`}>{data.club_name}</Link> : null;

const ClubsPanel: React.FC<{ data: IClubsData; currency: Currency }> = ({ data, currency }) => {
  const sorted = useMemo(() => [...data.top_clubs].sort((a, b) => b.charged - a.charged), [data.top_clubs]);

  const columns = useMemo<ColDef<ITopClub>[]>(() => [
    { headerName: "Club", field: "club_name", minWidth: 220, flex: 2, cellRenderer: ClubNameCell },
    { headerName: "Events", field: "events", type: "rightAligned", valueFormatter: (p) => formatNumber(p.value) },
    { headerName: "Transactions", field: "transactions", type: "rightAligned", valueFormatter: (p) => formatNumber(p.value) },
    { headerName: "Charged", field: "charged", type: "rightAligned", sort: "desc", valueFormatter: (p) => formatMoney(p.value, currency) },
    { headerName: "Eager Dog fees", field: "fees", type: "rightAligned", valueFormatter: (p) => formatMoney(p.value, currency) },
    { headerName: "Take rate", field: "take_rate", type: "rightAligned", valueFormatter: (p) => formatPercent(p.value) },
  ], [currency]);

  return (
    <div>
      <KpiRow>
        <KpiCard label="Total clubs" value={formatNumber(data.totals.total_clubs)} />
        <KpiCard label="New clubs" value={formatNumber(data.totals.new_clubs)} hint="Signed up in this period" />
        <KpiCard label="Active clubs" value={formatNumber(data.totals.active_clubs)} hint="Ran at least one event in this period" />
      </KpiRow>

      <div className={styles.grid}>
        <ChartCard
          wide
          title="Top clubs"
          subtitle="Events by start date; money from successful transactions in the selected currency and payment type"
          onExport={() => downloadCsv("top-clubs", sorted.map((c) => ({
            club: c.club_name,
            events: c.events,
            transactions: c.transactions,
            charged: c.charged,
            eager_dog_fees: c.fees,
            take_rate: (c.take_rate * 100).toFixed(2) + "%",
            refunded: c.refunded,
          })))}
        >
          <MetricsGrid rows={sorted} columns={columns} pageSize={PAGE_SIZE} />
        </ChartCard>

        <ChartCard
          wide
          title="New clubs by month"
          onExport={() => downloadCsv("new-clubs-by-month", data.new_clubs_by_month.map((r) => ({ month: r.month, new_clubs: r.count, total_clubs: r.cumulative })))}
        >
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.new_clubs_by_month.map((r) => ({ ...r, label: formatMonth(r.month) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} yAxisId="left" allowDecimals={false} />
              <YAxis {...AXIS_PROPS} yAxisId="right" orientation="right" allowDecimals={false} />
              <Tooltip {...TOOLTIP_PROPS} />
              <Legend {...LEGEND_PROPS} />
              <Bar yAxisId="left" dataKey="count" name="New clubs" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Total clubs" stroke={CHART_LINE} dot={false} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default ClubsPanel;
